import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import "./RelatorioFornecedor.css";

import {
  Button,
  TextField,
  Grid,
  MenuItem,
  Typography,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Container
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import dayjs from 'dayjs';
import api from '../../../services/api';
import API_CONFIG from '../../../config/api.config';

const RelatorioFornecedor = () => {
  const navigate = useNavigate();
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [prefeituraSelecionada, setPrefeituraSelecionada] = useState('');
  const [orgaoSelecionado, setOrgaoSelecionado] = useState('');
  const [fornecedorSelecionado, setFornecedorSelecionado] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  // Listas para os filtros
  const [prefeituras, setPrefeituras] = useState([]);
  const [orgaos, setOrgaos] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);

  // Verificação de autenticação e tipo de usuário
  useEffect(() => {
    const tipoUsuario = localStorage.getItem('tipoUsuario');
    const idUsuario = localStorage.getItem('idUsuario');

    if (!tipoUsuario || !idUsuario) {
      //console.log('Usuário não autenticado - Redirecionando para login');
      navigate('/', { replace: true });
      return;
    }

    if (tipoUsuario !== 'ADM') {
      //console.log(`Tipo de usuário (${tipoUsuario}) não corresponde ao permitido (ADM)`);
      navigate('/', { replace: true });
      return;
    }

    //console.log('ADM autenticado:', idUsuario);
  }, [navigate]);

  useEffect(() => {
    const handleMenuChange = (event) => {
      setIsMenuExpanded(!event.detail.isCollapsed);
    };
    window.addEventListener('menuStateChange', handleMenuChange);
    return () => window.removeEventListener('menuStateChange', handleMenuChange);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Carregar prefeituras
  useEffect(() => {
    carregarPrefeituras();
  }, []);

  // Carregar fornecedores
  useEffect(() => {
    carregarFornecedores();
  }, []);

  // Carregar órgãos quando uma prefeitura for selecionada
  useEffect(() => {
    if (prefeituraSelecionada) {
      carregarOrgaos(prefeituraSelecionada);
    } else {
      setOrgaos([]);
      setOrgaoSelecionado('');
    }
  }, [prefeituraSelecionada]);

  const carregarPrefeituras = async () => {
    try {
      const response = await api.get(API_CONFIG.ENDPOINTS.PREFEITURA.CONSULTAR_PREFEITURA);
      if (response.data.status && response.data.prefeituras) {
        // Verifica se prefeituras é um array
        const prefeiturasData = Array.isArray(response.data.prefeituras) 
          ? response.data.prefeituras 
          : [];
        setPrefeituras(prefeiturasData);
      } else {
        setPrefeituras([]);
      }
    } catch (error) {
      //console.error('Erro ao carregar prefeituras:', error);
      setError('Erro ao carregar prefeituras');
      setPrefeituras([]);
    }
  };

  const carregarFornecedores = async () => {
    try {
      const response = await api.get(API_CONFIG.ENDPOINTS.FORNECEDOR.CONSULTAR_FOR);
      if (response.data.status && response.data.fornecedores && response.data.fornecedores.data) {
        // Verifica se fornecedores.data é um array
        const fornecedoresData = Array.isArray(response.data.fornecedores.data) 
          ? response.data.fornecedores.data 
          : [];
        setFornecedores(fornecedoresData);
      } else {
        setFornecedores([]);
      }
    } catch (error) {
      //console.error('Erro ao carregar fornecedores:', error);
      setError('Erro ao carregar fornecedores');
      setFornecedores([]);
    }
  };

  const carregarOrgaos = async (prefeituraId) => {
    try {
      // Usar diretamente o endpoint já que a função pode não estar sendo reconhecida
      const endpoint = `/api/orgaosPrefeitura/${prefeituraId}`;
      //console.log('Endpoint para carregar órgãos:', endpoint);
      
      const response = await api.get(endpoint);
      
      // Verificar se a resposta tem a estrutura esperada
      if (response.data.status && response.data.prefeitura && response.data.prefeitura.orgaos) {
        // A API retorna os órgãos dentro do objeto prefeitura
        const orgaosData = Array.isArray(response.data.prefeitura.orgaos) 
          ? response.data.prefeitura.orgaos 
          : [];
        setOrgaos(orgaosData);
        //console.log('Órgãos carregados:', orgaosData);
      } else if (response.data.status && response.data.orgaos) {
        // Fallback para estrutura alternativa
        const orgaosData = Array.isArray(response.data.orgaos) 
          ? response.data.orgaos 
          : [];
        setOrgaos(orgaosData);
        //console.log('Órgãos carregados (fallback):', orgaosData);
      } else {
        setOrgaos([]);
        //console.log('Nenhum órgão encontrado');
        //console.log('Estrutura da resposta:', response.data);
      }
    } catch (error) {
      //console.error('Erro ao carregar órgãos:', error);
      setError('Erro ao carregar órgãos');
      setOrgaos([]);
    }
  };

  const handleLimparFiltros = () => {
    setDataInicio('');
    setDataFim('');
    setPrefeituraSelecionada('');
    setOrgaoSelecionado('');
    setFornecedorSelecionado('');
    setError('');
    setSuccess('');
  };

  const handleDownloadPDF = async () => {
    // Validação obrigatória
    if (!prefeituraSelecionada) {
      setError('Selecione uma prefeitura para gerar o relatório.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const params = {};
      if (dataInicio) params.data_inicio = dataInicio;
      if (dataFim) params.data_fim = dataFim;
      if (prefeituraSelecionada) params.PRE_ID = prefeituraSelecionada;
      if (orgaoSelecionado) params.ORG_ID = orgaoSelecionado;
      if (fornecedorSelecionado) params.FOR_ID = fornecedorSelecionado;

      const response = await api.get(API_CONFIG.ENDPOINTS.RELATORIOS.COMPRAS_ADM, {
        params,
        responseType: 'blob'
      });

      // Verificar se a resposta é um PDF válido
      if (response.data.type && response.data.type.includes('application/pdf')) {
        // Criar URL do blob
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        
        // Detectar se o navegador consegue renderizar PDFs
        const canRenderPDF = () => {
          // Chrome, Edge, Firefox modernos conseguem renderizar PDFs
          const userAgent = navigator.userAgent.toLowerCase();
          return userAgent.includes('chrome') || 
                 userAgent.includes('edge') || 
                 userAgent.includes('firefox');
        };

        if (canRenderPDF()) {
          // Tentar abrir em nova aba
          const newWindow = window.open(url, '_blank');
          
          if (newWindow) {
            // Aguardar um pouco para ver se conseguiu abrir
            setTimeout(() => {
              if (newWindow.closed) {
                // Se a janela foi fechada rapidamente, provavelmente não conseguiu renderizar
                downloadPDF(url);
              }
            }, 1000);
            
            setSuccess('Relatório aberto em nova aba!');
          } else {
            // Popup bloqueado, fazer download
            downloadPDF(url);
            setSuccess('Relatório baixado com sucesso! (popup bloqueado)');
          }
        } else {
          // Navegadores que não conseguem renderizar PDFs, fazer download direto
          downloadPDF(url);
          setSuccess('Relatório baixado com sucesso!');
        }
        
        // Limpar URL após um tempo
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 5000);
      } else {
        // Se não for PDF, tentar ler como JSON para pegar mensagem de erro
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            setError(errorData.message || 'Erro ao gerar relatório');
          } catch {
            setError('Erro ao gerar relatório PDF');
          }
        };
        reader.readAsText(response.data);
      }
    } catch (error) {
      //console.error('Erro ao baixar PDF:', error);
      
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.response?.data) {
        // Se data for um objeto, tentar converter para string
        if (typeof error.response.data === 'object') {
          setError('Erro ao gerar relatório PDF');
        } else {
          setError(String(error.response.data));
        }
      } else {
        setError('Erro ao gerar relatório PDF');
      }
    } finally {
      setLoading(false);
    }
  };

  // Função auxiliar para fazer download do PDF
  const downloadPDF = (url) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `relatorio-fornecedor-${new Date().toISOString().split('T')[0]}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className={`relatorio-fornecedor-container ${isMenuExpanded ? 'menu-expanded' : ''}`}>
      <div className="relatorio-fornecedor-wrapper">
        <h1 className="relatorio-fornecedor-title">Relatório Fornecedor</h1>
        
        <Container maxWidth="lg" className="relatorio-fornecedor-content">
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <Card className="relatorio-fornecedor-card">
            <CardContent>
              <Typography variant="h6" gutterBottom className="relatorio-fornecedor-subtitle">
                Filtros do Relatório
              </Typography>
              
              <Grid container spacing={5} sx={{ mb: 4 }} justifyContent="center">
                <Grid item xs={12} sm={6} md={2.5}>
                  <TextField
                    label="Data Início"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.5}>
                  <TextField
                    label="Data Fim"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>
                      <>Prefeitura <span style={{ color: 'red' }}>*</span></>
                    </InputLabel>
                    <Select
                      value={prefeituraSelecionada}
                      onChange={(e) => setPrefeituraSelecionada(e.target.value)}
                      label="Prefeitura"
                    >
                      <MenuItem value="">
                       
                      </MenuItem>
                      {prefeituras && prefeituras.length > 0 ? (
                        prefeituras.map((prefeitura) => (
                          <MenuItem key={prefeitura.PRE_ID} value={prefeitura.PRE_ID}>
                            {prefeitura.PRE_NOME}
                          </MenuItem>
                        ))
                      ) : null}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Órgão</InputLabel>
                    <Select
                      value={orgaoSelecionado}
                      onChange={(e) => setOrgaoSelecionado(e.target.value)}
                      label="Órgão"
                      disabled={!prefeituraSelecionada}
                    >
                      <MenuItem value="">
                       
                      </MenuItem>
                      {orgaos && orgaos.length > 0 ? (
                        orgaos.map((orgao) => (
                          <MenuItem key={orgao.ORG_ID} value={orgao.ORG_ID}>
                            {orgao.ORG_SIGLA} - {orgao.ORG_DESCRICAO}
                          </MenuItem>
                        ))
                      ) : null}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Fornecedor</InputLabel>
                    <Select
                      value={fornecedorSelecionado}
                      onChange={(e) => setFornecedorSelecionado(e.target.value)}
                      label="Fornecedor"
                    >
                      <MenuItem value="">
                        <em>Todos os fornecedores</em>
                      </MenuItem>
                      {fornecedores && fornecedores.length > 0 ? (
                        fornecedores.map((fornecedor) => (
                          <MenuItem key={fornecedor.FOR_ID} value={fornecedor.FOR_ID}>
                            {fornecedor.FOR_RAZAO_SOCIAL}
                          </MenuItem>
                        ))
                      ) : null}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={handleLimparFiltros}
                  disabled={loading}
                  size="large"
                >
                  Limpar Filtros
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleDownloadPDF}
                  disabled={loading}
                  size="large"
                  className="relatorio-fornecedor-export-button"
                >
                  {loading ? 'Gerando PDF...' : 'Abrir Relatório PDF'}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card className="relatorio-fornecedor-info-card">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informações do Relatório
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Este relatório permite gerar um documento PDF com todas as compras realizadas por fornecedores específicos, 
                filtradas por período, prefeitura, órgão e fornecedor. O relatório inclui informações detalhadas sobre 
                cada compra, incluindo valores, status e informações do fornecedor.
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </div>
    </div>
  );
};

export default RelatorioFornecedor; 